import { useEffect, useRef, useState, type ReactNode } from 'react';

import { IdleWarningModal } from '@/components/auth/IdleWarningModal';
import { createIdleTimer, type IdleTimerHandle } from '@/lib/auth/idleTimer';
import { buildLoginRedirect } from '@/lib/auth/loginRedirect';
import { subscribePresence } from '@/lib/auth/presenceSignal';
import { resolveLogoutDestination } from '@/lib/auth/resolveLogoutDestination';
import { cancelProactiveRefresh } from '@/lib/auth/tokenRefresh';
import { clearAuthStorage, useAuthStore } from '@/stores/authStore';

// 1 hr idle before logout, with a 2 min warning countdown beforehand.
const IDLE_MS = 60 * 60_000;
const WARNING_MS = 2 * 60_000;
const ACTIVITY_DEBOUNCE_MS = 250;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

function debounce<T extends (...args: never[]) => void>(fn: T, wait: number): T {
  let pending: ReturnType<typeof setTimeout> | null = null;
  return ((...args: Parameters<T>) => {
    if (pending !== null) clearTimeout(pending);
    pending = setTimeout(() => {
      fn(...args);
    }, wait);
  }) as T;
}

interface IdleLogoutProviderProps {
  children: ReactNode;
}

export function IdleLogoutProvider({ children }: IdleLogoutProviderProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [warning, setWarning] = useState(false);
  const timerRef = useRef<IdleTimerHandle | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      timerRef.current?.stop();
      // Clear stale warning state on auth flip so re-login doesn't show
      // a leftover modal from a previous session.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setWarning(false);
      return;
    }

    const handleLockout = (): void => {
      // Storage-only wipe + replace() — see authStore.ts:88-99 for the
      // first-paint-flash reasoning. buildLoginRedirect preserves the deep
      // link so the user lands back on this page after re-login.
      cancelProactiveRefresh();
      const { user } = useAuthStore.getState();
      const dest = resolveLogoutDestination(user);
      const target = buildLoginRedirect(`${dest}?reason=idle`);
      clearAuthStorage();
      window.location.replace(target);
    };

    const timer = createIdleTimer({
      idleMs: IDLE_MS,
      warningMs: WARNING_MS,
      onWarning: () => {
        setWarning(true);
      },
      onLockout: handleLockout,
    });
    timerRef.current = timer;

    const resetAll = (): void => {
      setWarning(false);
      timer.reset();
    };
    const debouncedReset = debounce(resetAll, ACTIVITY_DEBOUNCE_MS);

    // A live call emits none of ACTIVITY_EVENTS, so being on camera for an
    // hour used to look exactly like an abandoned tab and the lockout fired
    // at the end of a 60-minute session. useCallKeepAlive publishes presence
    // while the LiveKit room is connected, from this tab or from the sibling
    // tab openSessionCallTab spawns for staff calls. Gated on the user id so
    // one account's call cannot hold another account's session open on a
    // shared browser. Not debounced: the publisher already throttles.
    const stopPresence = subscribePresence((userId) => {
      if (userId !== useAuthStore.getState().user?.id) return;
      resetAll();
    });

    ACTIVITY_EVENTS.forEach((e) => {
      window.addEventListener(e, debouncedReset, { passive: true });
    });
    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') debouncedReset();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    timer.start();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => {
        window.removeEventListener(e, debouncedReset);
      });
      document.removeEventListener('visibilitychange', handleVisibility);
      stopPresence();
      timer.stop();
      timerRef.current = null;
    };
  }, [isAuthenticated]);

  const handleStay = (): void => {
    setWarning(false);
    timerRef.current?.reset();
  };

  return (
    <>
      {children}
      <IdleWarningModal open={warning} onStay={handleStay} countdownSeconds={WARNING_MS / 1000} />
    </>
  );
}
